import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles, Scissors } from "lucide-react";
import { PageHero } from "@/components/common/PageHero";
import { Reveal } from "@/components/common/Reveal";
import { CtaBand } from "@/components/common/CtaBand";
import { Seo } from "@/components/common/Seo";
import { buildBreadcrumbSchema } from "@/lib/schema";
import { SKIN_TREATMENTS, HAIR_TREATMENTS } from "@/data/site";

const Grid = ({ items }) => (
  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
    {items.map((t, i) => (
      <Reveal key={t.slug} delay={(i % 3) * 0.06}>
        <Link
          to={`/${t.slug}`}
          data-testid={`aesthetic-item-${t.slug}`}
          className="group flex h-full flex-col overflow-hidden rounded-2xl border border-brand-line bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_30px_60px_-35px_rgba(2,125,199,0.5)]"
        >
          <div className="aspect-[16/10] overflow-hidden">
            <img
              src={t.image}
              alt={`${t.name} in Varanasi — Dr. Ankesh Seth`}
              loading="lazy"
              width="640"
              height="400"
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
          </div>
          <div className="flex flex-1 flex-col p-7">
            <h3 className="font-display text-2xl font-medium text-brand-dark">{t.name}</h3>
            <p className="mt-2 flex-1 text-sm text-brand-muted">{t.short}</p>
            <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-brand-blue">
              Read More
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </span>
          </div>
        </Link>
      </Reveal>
    ))}
  </div>
);

export default function AestheticTreatments() {
  return (
    <div data-testid="aesthetic-treatments-page">
      <Seo
        title="Skin & Hair Treatments in Varanasi | Dr. Ankesh Seth"
        description="Advanced skin and hair treatments in Varanasi — HydraFacial, chemical peels, Botox & fillers, laser mole removal, PRP/GFC hair therapy, hair transplant and more at Dr. Ankesh Seth's NowTreat Skin & Hair Clinic, Paharia."
        path="/aesthetic-treatments"
        jsonLd={buildBreadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Aesthetic Treatments", path: "/aesthetic-treatments" },
        ])}
      />
      <PageHero
        eyebrow="Skin & Hair"
        titleLines={["Aesthetic Skin &", "Hair Treatments"]}
        subtitle="Look and feel your best with advanced, medically-guided skin and hair treatments in Varanasi — from glowing facials and anti-ageing injectables to PRP hair therapy and transplants."
        crumbs={[{ label: "Aesthetic Treatments" }]}
      />

      <section className="bg-white">
        <div className="mx-auto max-w-[1440px] px-6 py-24 md:px-12 md:py-28 lg:px-20">
          <Reveal className="mb-10 flex items-center gap-3">
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-blue/10 text-brand-blue">
              <Sparkles className="h-6 w-6" />
            </span>
            <div>
              <h2 className="font-display text-3xl font-semibold text-brand-dark md:text-4xl">Skin Treatments</h2>
              <p className="mt-1 text-brand-muted">Medical-grade skincare for a clear, radiant, youthful complexion.</p>
            </div>
          </Reveal>
          <Grid items={SKIN_TREATMENTS} />
        </div>
      </section>

      <section className="bg-brand-soft">
        <div className="mx-auto max-w-[1440px] px-6 py-24 md:px-12 md:py-28 lg:px-20">
          <Reveal className="mb-10 flex items-center gap-3">
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-green/15 text-brand-green">
              <Scissors className="h-6 w-6" />
            </span>
            <div>
              <h2 className="font-display text-3xl font-semibold text-brand-dark md:text-4xl">Hair Treatments</h2>
              <p className="mt-1 text-brand-muted">Restore, strengthen and regrow your hair with advanced therapies.</p>
            </div>
          </Reveal>
          <Grid items={HAIR_TREATMENTS} />
        </div>
      </section>

      <CtaBand title="Ready to look and feel your best?" text="Book a skin or hair consultation with Dr. Ankesh Seth — call or WhatsApp today for a personalised plan." />
    </div>
  );
}
